import { toast } from 'sonner';
import type { AuthAllowlistMode } from '@/lib/types';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { DomainEditor } from '@/components/auth/DomainEditor';
import { OrgCreationPolicy } from '@/components/auth/OrgCreationPolicy';
import { AuthProviderList } from '@/components/auth/AuthProviderList';
import { useAuthSettings, useUpdateAuthSettings } from '@/hooks/auth/useAuthSettings';

export function AdminAuthPage() {
  const settingsQuery = useAuthSettings();
  const updateMutation = useUpdateAuthSettings();

  async function handleSave(domains: string[], mode: AuthAllowlistMode, emails: string[]) {
    await updateMutation.mutateAsync(
      { allowedEmailDomains: domains, allowedEmails: emails, mode },
      {
        onSuccess: () => toast.success('Auth settings saved'),
        onError: (err) =>
          toast.error('Failed to save auth settings', {
            description: err.message,
          }),
      },
    );
  }

  async function handleToggleOrgCreation(allow: boolean) {
    const current = settingsQuery.data;
    if (!current) return;
    await updateMutation.mutateAsync(
      {
        allowedEmailDomains: current.allowedEmailDomains,
        allowedEmails: current.allowedEmails,
        mode: current.mode,
        allowSelfServeOrgCreation: allow,
      },
      {
        onSuccess: () =>
          toast.success(
            allow
              ? 'Self-serve organization creation enabled'
              : 'Self-serve organization creation disabled',
          ),
        onError: (err) =>
          toast.error('Failed to save auth settings', {
            description: err.message,
          }),
      },
    );
  }

  return (
    <AdminSubPageLayout
      title="Auth"
      description="Control which email domains and individual email addresses are permitted to sign in. Applies to every OAuth provider on this instance."
    >
      <AsyncContent
        isLoading={settingsQuery.isLoading}
        isError={settingsQuery.isError}
        loadingMessage="Loading auth settings…"
        errorMessage="Failed to load auth settings."
      >
        {settingsQuery.data && (
          <div className="space-y-4">
            <DomainEditor
              key={`${settingsQuery.data.updatedAt}:${settingsQuery.data.mode}:${settingsQuery.data.allowedEmailDomains.join(',')}:${settingsQuery.data.allowedEmails.join(',')}`}
              initial={settingsQuery.data.allowedEmailDomains}
              initialEmails={settingsQuery.data.allowedEmails}
              initialMode={settingsQuery.data.mode}
              onSave={handleSave}
              isSaving={updateMutation.isPending}
            />
            <OrgCreationPolicy
              allowSelfServeOrgCreation={settingsQuery.data.allowSelfServeOrgCreation}
              onChange={handleToggleOrgCreation}
              isSaving={updateMutation.isPending}
            />
          </div>
        )}
      </AsyncContent>
      <AuthProviderList />
    </AdminSubPageLayout>
  );
}
